import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "../../../../../ui/button";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";

/**
 * Inline editor for string[] fields (`pill_chips`, `bullets`, `step_labels.labels`).
 * Renders existing entries as removable chips and provides an inline "add"
 * input. Reads/writes via `form.setValue` so RHF's validation still sees the
 * latest array.
 */
const StringListEditor = ({
  form,
  name,
  label,
  placeholder = "Add an entry…",
  hint,
  max,
}) => {
  const values = form.watch(name) || [];
  const [draft, setDraft] = useState("");
  const errors = name
    .split(".")
    .reduce((acc, key) => acc?.[key], form.formState.errors);

  const setList = (next) =>
    form.setValue(name, next, {
      shouldDirty: true,
      shouldValidate: true,
    });

  const addChip = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (typeof max === "number" && values.length >= max) return;
    if (values.includes(trimmed)) {
      setDraft("");
      return;
    }
    setList([...values, trimmed]);
    setDraft("");
  };

  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addChip();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={addChip}
          disabled={!draft.trim() || (typeof max === "number" && values.length >= max)}
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {values.map((entry, idx) => (
            <span
              key={`${entry}-${idx}`}
              className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs"
            >
              {entry}
              <button
                type="button"
                onClick={() => setList(values.filter((_, i) => i !== idx))}
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${entry}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {typeof max === "number" && (
        <p className="text-xs text-muted-foreground">
          {values.length} / {max} entries
        </p>
      )}
      {errors?.message && (
        <p className="text-xs text-destructive">{errors.message}</p>
      )}
    </div>
  );
};

export default StringListEditor;
